import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatDate, formatDateTime, formatNumber } from './format';
import useWarehouseEditAccess from './useWarehouseEditAccess';

export const OUTCOME_ORDER = [
  'REQUIRED REPLENISHMENT',
  'EFFICIENCY REPLENISHMENT',
  'LOCAL RESERVE TOP-UP',
  'INSUFFICIENT RESERVE',
  'OPERATOR DECISION REQUIRED',
  'CAPACITY / DATA REVIEW',
  'SAME-SKU CONSOLIDATION',
  'MIXED-PALLET CONSOLIDATION',
  'DEEP RESERVE',
  'DEEP RESERVE BLOCKED',
  'HEALTHY',
];

export const OUTCOME_STYLE = {
  'REQUIRED REPLENISHMENT': { fill: '#fecaca', stroke: '#dc2626', badge: 'bg-red-100 text-red-800', swatch: 'bg-red-200 border-red-500' },
  'EFFICIENCY REPLENISHMENT': { fill: '#fde68a', stroke: '#d97706', badge: 'bg-amber-100 text-amber-800', swatch: 'bg-amber-200 border-amber-500' },
  'LOCAL RESERVE TOP-UP': { fill: '#99f6e4', stroke: '#0d9488', badge: 'bg-teal-100 text-teal-800', swatch: 'bg-teal-200 border-teal-500' },
  'INSUFFICIENT RESERVE': { fill: '#fed7aa', stroke: '#d97706', badge: 'bg-orange-100 text-orange-800', swatch: 'bg-orange-200 border-orange-500' },
  'OPERATOR DECISION REQUIRED': { fill: '#fef08a', stroke: '#ca8a04', badge: 'bg-yellow-100 text-yellow-800', swatch: 'bg-yellow-200 border-yellow-500' },
  'CAPACITY / DATA REVIEW': { fill: '#e5e7eb', stroke: '#9ca3af', hatched: true, badge: 'bg-gray-100 text-gray-700', swatch: 'bg-gray-200 border-gray-400' },
  'SAME-SKU CONSOLIDATION': { fill: '#bae6fd', stroke: '#0284c7', hatched: false, badge: 'bg-sky-100 text-sky-800', swatch: 'bg-sky-200 border-sky-500' },
  'MIXED-PALLET CONSOLIDATION': { fill: '#ddd6fe', stroke: '#7c3aed', hatched: false, badge: 'bg-violet-100 text-violet-800', swatch: 'bg-violet-200 border-violet-500' },
  'DEEP RESERVE': { fill: '#fde68a', stroke: '#d97706', hatched: false, badge: 'bg-amber-100 text-amber-800', swatch: 'bg-amber-200 border-amber-500' },
  'DEEP RESERVE BLOCKED': { fill: '#fed7aa', stroke: '#c2410c', hatched: false, badge: 'bg-orange-100 text-orange-800', swatch: 'bg-orange-200 border-orange-500' },
  HEALTHY: { fill: '#bbf7d0', stroke: '#059669', badge: 'bg-emerald-100 text-emerald-800', swatch: 'bg-emerald-200 border-emerald-500' },
};

export const REASON_LABELS = {
  stale_sync: 'ShipHero sync not fresh',
  stale_inventory: 'Inventory row stale',
  manual_capacity: 'Manual capacity — operator decision',
  missing_capacity_profile: 'No capacity profile',
  invalid_capacity_profile: 'Capacity profile has no max units',
  capacity_family_missing: 'SKU family not in capacity matrix',
  capacity_not_governed: 'No governed capacity for this location type',
  unknown_location_type: 'Location type unresolved (Pallet / SM Bin / LG Bin)',
  capacity_governance: 'Capacity matrix row not approved',
  location_governance: 'Location not approved',
  pickable_drift: 'ShipHero / Eadin pickable drift',
  missing_product_mapping: 'SKU not in Product Catalog',
  stale_sales: 'Sales series not fresh',
  ambiguous_capacity_family: 'Multiple capacity profiles match SKU family',
  bin_size_ineligible: 'SKU family not allowed in this bin size',
  safety_fill: 'Below safety fill',
  critical_supply: 'Critical days of supply',
  shared_reserve: 'Shares reserve work',
  shared_rack: 'Shares rack work',
  efficiency_deferred: 'Efficiency move deferred',
  multi_sku_pickable: 'Multiple SKUs in pick location',
  partial_reserve: 'Reserve covers only part of the need',
  local_reserve_top_up: 'Local Reserve top-up',
  local_reserve_seed: 'Seed Local Reserve',
  local_reserve_below_refill: 'Below one full refill',
  capacity_limited: 'Limited by shared F capacity',
  missing_carton_qty: 'Missing carton quantity',
  conflicting_carton_qty: 'Conflicting carton quantities',
  unverified_shared_capacity: 'Shared capacity cannot be verified',
  no_open_deep_reserve_location: 'No Level 4 location is open for Deep Reserve',
  no_level3_source: 'No available Level 3 source holds this SKU',
  excess_below_one_carton: 'Less than one whole carton can move',
  sales_not_matched: 'Product IDs are not matched to daily sales',
  sales_history_incomplete: '365-day sales history is incomplete',
  sales_stale: 'Sales sync is not current',
  missing_lifecycle_status: 'Product status is missing or unrecognized',
  planned_launch_stock: 'Planned-launch stock needs Ernie’s decision',
  verified_zero_demand: 'Verified zero demand needs Ernie’s decision',
  mixed_pallet: 'Compatible same-family pallet consolidation',
  mixed_capacity_profiles: 'Family spans multiple capacity profiles',
  key_collision: 'Recommendation key already exists',
  stale: 'Sync or sales data is not fresh',
};

export const isLocalReserveBlocker = (row) => (
  row?.outcome === 'CAPACITY / DATA REVIEW' && row?.inputs?.role === 'local_reserve'
);

export const useReplenishmentPlan = ({ enabled }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/v1/warehouse/replenishment');
      setPlan(data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to load replenishment plan');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (enabled && !plan) refresh();
  }, [enabled, plan, refresh]);
  const outcomeByLocation = useMemo(() => {
    const map = new Map();
    (plan?.recommendations || []).forEach((row) => {
      const rank = OUTCOME_ORDER.indexOf(row.outcome);
      const current = map.get(row.location_code);
      if (!current || rank < OUTCOME_ORDER.indexOf(current)) map.set(row.location_code, row.outcome);
    });
    return map;
  }, [plan]);
  return { plan, loading, error, refresh, outcomeByLocation };
};

export const movesForRecommendation = (row) => (row.sources || [])
  .filter((source) => source.take_qty > 0)
  .map((source) => ({
    from: source.location_code,
    to: row.location_code,
    sku: row.sku,
    quantity: source.take_qty,
    rankBasis: source.rank_basis || null,
  }));

const pct = (value) => (value == null ? '—' : `${Math.round(Number(value) * 100)}%`);

const workGroupText = (value) => {
  if (!value) return '';
  if (value.startsWith('RSV:')) return `Reserve ${value.slice(4)}`;
  if (value.startsWith('RACK:')) return `Rack ${value.slice(5)}`;
  if (value.startsWith('LOC:')) return value.slice(4);
  return value;
};

const ResolveForm = ({ row, onDone, onCancel }) => {
  const [status, setStatus] = useState('completed');
  const [actualQty, setActualQty] = useState(row.recommended_qty ?? 0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { status, note };
      if (status !== 'dismissed') body.actual_qty = Number(actualQty);
      await axios.patch(`/api/v1/warehouse/replenishment/${row.id}`, body);
      onDone();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to record outcome');
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-2 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-[11px]">
      <div className="flex gap-2">
        <select aria-label="Outcome" value={status} onChange={(event) => setStatus(event.target.value)} className="h-7 rounded border border-gray-300 bg-white px-1 text-[11px]">
          <option value="completed">Completed as recommended</option>
          <option value="overridden">Overridden (different qty / source)</option>
          <option value="dismissed">Dismissed</option>
        </select>
        {status !== 'dismissed' && <input aria-label="Units moved" type="number" min="0" step="1" value={actualQty} onChange={(event) => setActualQty(event.target.value)} className="h-7 w-20 rounded border border-gray-300 px-1 text-[11px]" />}
      </div>
      <textarea aria-label="Reason" required value={note} onChange={(event) => setNote(event.target.value)} placeholder="Reason (required, recorded in the governed change log)" rows={2} className="w-full rounded border border-gray-300 px-1.5 py-1 text-[11px]" />
      {error && <p className="text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded px-2 py-1 text-gray-600 hover:bg-gray-200">Cancel</button>
        <button type="submit" disabled={saving || !note.trim()} className="rounded bg-black px-2 py-1 font-medium text-white disabled:opacity-40">{saving ? 'Saving…' : 'Record'}</button>
      </div>
    </form>
  );
};

const DEEP_RESERVE_MOVE_LABELS = {
  whole_pallet: 'Existing pallet',
  pallet_load: 'Pallet built downstairs',
  mixed_pallet_load: 'Mixed pallet built downstairs',
};

const RecommendationRow = ({ row, active, canEdit, onFocus, onResolved }) => {
  const [resolving, setResolving] = useState(false);
  const moves = movesForRecommendation(row);
  const moveUnit = row.inputs?.move_unit;
  const moveLabel = DEEP_RESERVE_MOVE_LABELS[moveUnit] || 'Pallet move';
  const moveQty = row.inputs?.move_qty ?? row.recommended_qty;
  const cartonDetail = row.inputs?.cartons != null && row.inputs?.units_per_carton != null
    ? ` (${formatNumber(row.inputs.cartons)} cartons × ${formatNumber(row.inputs.units_per_carton)} units)`
    : '';
  return (
    <li className={`rounded-md border px-2.5 py-2 ${active ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white'}`}>
      <button type="button" onClick={() => onFocus(row)} className="w-full text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs font-semibold text-gray-900"><span className={`mr-1.5 inline-block h-2 w-2 rounded-sm border ${(OUTCOME_STYLE[row.outcome] || OUTCOME_STYLE.HEALTHY).swatch}`} />{row.location_code}{row.inputs?.role === 'local_reserve' && <span className="ml-1.5 rounded bg-amber-50 px-1 py-0.5 font-sans text-[10px] font-medium text-amber-700">LR top-up</span>}</span>
          <span className="text-[11px] text-gray-500">Current / Capacity {formatNumber(row.current_qty)}{row.governed_capacity ? ` / ${formatNumber(row.governed_capacity)}` : ''} · {pct(row.fill_pct)} · DoS {row.inputs?.days_of_supply == null ? '—' : `${Number(row.inputs.days_of_supply).toFixed(1)}d (${row.inputs?.governed_basis || 'rate'})`}</span>
        </div>
        <div className="mt-0.5 truncate text-[11px] text-gray-600">{row.sku}{row.product_name ? ` · ${row.product_name}` : ''}</div>
        {row.outcome === 'DEEP RESERVE' && <div className="mt-1 text-[11px] text-gray-800">
          Move {moveLabel}, {formatNumber(moveQty)} units{cartonDetail}
          {' '}from Level 3 {moves.map((move) => `${move.from} (${formatNumber(move.quantity)})`).join(', ')}
          {' '}to Level 4 {row.inputs?.destination_location || row.location_code}
          {' '}· Keep {formatNumber(row.inputs?.retained_accessible_qty)} accessible
        </div>}
        {moves.length > 0 && row.outcome !== 'DEEP RESERVE' && <div className="mt-1 text-[11px] text-gray-800">Move {formatNumber(row.recommended_qty)} from {moves.map((move) => `${move.from} (${formatNumber(move.quantity)}${move.rankBasis ? `, ${move.rankBasis}` : ''})`).join(', ')}</div>}
        {row.inputs?.work_group && <div className="mt-1 text-[11px] text-gray-500">Work Group {workGroupText(row.inputs.work_group)}</div>}
        {row.reasons?.length > 0 && <div className="mt-1 text-[11px] text-gray-500">{row.reasons.map((reason) => REASON_LABELS[reason] || reason).join(' · ')}</div>}
        {row.status !== 'open' && <div className="mt-1 text-[11px] text-gray-500">{row.status} by {row.resolved_by} · {formatDateTime(row.resolved_at)}{row.actual_qty != null ? ` · ${formatNumber(row.actual_qty)} units` : ''}</div>}
      </button>
      {canEdit && row.status === 'open' && row.outcome !== 'HEALTHY' && !resolving && (
        <button type="button" onClick={() => setResolving(true)} className="mt-1.5 text-[11px] font-medium text-blue-700 hover:underline">Record outcome</button>
      )}
      {resolving && <ResolveForm row={row} onCancel={() => setResolving(false)} onDone={() => { setResolving(false); onResolved(); }} />}
    </li>
  );
};

const ReplenishmentPanel = ({ plan, loading, error, activeLocation, onRefresh, onFocus, onClose }) => {
  const canEdit = useWarehouseEditAccess();
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState('');
  const grouped = useMemo(() => {
    const groups = new Map(OUTCOME_ORDER.map((outcome) => [outcome, []]));
    (plan?.recommendations || []).forEach((row) => {
      const outcome = isLocalReserveBlocker(row) ? 'LOCAL RESERVE TOP-UP' : row.outcome;
      groups.get(outcome)?.push(row);
    });
    groups.get('REQUIRED REPLENISHMENT')?.sort((a, b) => (a.fill_pct ?? 1) - (b.fill_pct ?? 1));
    return groups;
  }, [plan]);
  const runNow = async () => {
    setRunning(true);
    setRunError('');
    try {
      await axios.post('/api/v1/warehouse/replenishment/run');
      await onRefresh();
    } catch (requestError) {
      setRunError(requestError.response?.data?.error || 'Unable to run the plan');
    } finally {
      setRunning(false);
    }
  };
  const healthy = grouped.get('HEALTHY') || [];
  const localReserveBlockerCount = (plan?.recommendations || []).filter(isLocalReserveBlocker).length;
  const outcomeCount = (outcome) => {
    const count = plan?.summary?.byOutcome?.[outcome] ?? 0;
    return outcome === 'CAPACITY / DATA REVIEW'
      ? Math.max(0, count - localReserveBlockerCount)
      : count;
  };
  return (
    <aside className="absolute inset-y-0 left-0 z-30 flex w-[400px] flex-col border-r border-gray-200 bg-white shadow-xl">
      <div className="flex items-start justify-between border-b border-gray-200 px-3 py-2.5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Pickable replenishment</p>
          <h3 className="mt-0.5 text-[16px] font-semibold text-gray-900">{plan?.planDate ? `Plan for ${formatDate(plan.planDate)}` : 'No plan yet'}</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {plan?.generatedAt ? `Generated ${formatDateTime(plan.generatedAt)}` : 'Runs each morning after the ShipHero locations sync'}
            {plan && !plan.syncFresh && <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800">ShipHero sync not fresh</span>}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onRefresh} aria-label="Refresh plan" className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
          <button type="button" onClick={onClose} aria-label="Close replenishment" className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><XMarkIcon className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 border-b border-gray-200 px-3 py-2">
        {OUTCOME_ORDER.map((outcome) => <span key={outcome} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${OUTCOME_STYLE[outcome].badge}`}>{outcome} · {outcomeCount(outcome)}</span>)}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {error && <p className="text-xs text-red-600">{error}</p>}
        {!error && plan && plan.recommendations.length === 0 && <p className="text-xs text-gray-500">No pickable locations evaluated yet. {canEdit ? 'Run the plan to evaluate the current ShipHero snapshot.' : ''}</p>}
        {OUTCOME_ORDER.filter((outcome) => outcome !== 'HEALTHY').map((outcome) => {
          const rows = grouped.get(outcome) || [];
          if (!rows.length) return null;
          return (
            <section key={outcome} className="mb-3">
              <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400">{outcome} · {rows.length}</h4>
              <ul className="space-y-1.5">
                {rows.map((row) => <RecommendationRow key={row.id} row={row} active={activeLocation === row.location_code} canEdit={canEdit} onFocus={onFocus} onResolved={onRefresh} />)}
              </ul>
            </section>
          );
        })}
        {healthy.length > 0 && <p className="text-[11px] text-gray-500">{healthy.length} healthy pick {healthy.length === 1 ? 'location' : 'locations'} above the governed Smart Restock triggers.</p>}
      </div>
      <div className="border-t border-gray-200 px-3 py-2 text-[11px] text-gray-500">
        <div className="flex items-center justify-between gap-2">
          <span>Eadin calculates · humans move in ShipHero · ShipHero zero alerts stay on.</span>
          {canEdit && <button type="button" onClick={runNow} disabled={running} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">{running ? 'Running…' : 'Run now'}</button>}
        </div>
        {runError && <p className="mt-1 text-red-600">{runError}</p>}
      </div>
    </aside>
  );
};

export default ReplenishmentPanel;
